import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import type { DuplicateMatch } from './hooks/useContacts';

interface DuplicateWarningModalProps {
  open: boolean;
  duplicates: DuplicateMatch[];
  /** Whether the create request is currently in flight (after "Create anyway"). */
  isSaving?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function fullName(match: DuplicateMatch): string {
  const name = `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim();
  return name || 'Unnamed contact';
}

/**
 * DuplicateWarningModal — shown before creating a contact when another contact
 * already has the same email and/or phone. Lists each match (name + the field
 * that collided + its value) and asks the user whether to proceed anyway.
 */
export function DuplicateWarningModal({
  open,
  duplicates,
  isSaving = false,
  onConfirm,
  onCancel,
}: DuplicateWarningModalProps) {
  const hasEmail = duplicates.some((d) => d.matchField === 'email');
  const hasPhone = duplicates.some((d) => d.matchField === 'phone');
  const fieldSummary =
    hasEmail && hasPhone ? 'email and phone number' : hasEmail ? 'email' : 'phone number';

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Possible duplicate contact"
      size="md"
      closeOnOverlayClick={!isSaving}
      data-testid="contact-duplicate-warning"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          The {fieldSummary} you entered already{' '}
          {duplicates.length > 1 ? 'belong to existing contacts' : 'belongs to an existing contact'}
          :
        </p>

        <ul className="space-y-2">
          {duplicates.map((d) => (
            <li
              key={`${d.id}-${d.matchField}`}
              className="rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm dark:border-border-dark dark:bg-surface-dark-tertiary"
            >
              <span className="font-medium text-gray-900 dark:text-gray-100">{fullName(d)}</span>
              <span className="text-gray-500 dark:text-gray-400">
                {' '}
                — matches on {d.matchField === 'email' ? 'email' : 'phone number'}
                {d.matchField === 'email' && d.email ? ` (${d.email})` : ''}
                {d.matchField === 'phone' && d.phone ? ` (${d.phone})` : ''}
              </span>
            </li>
          ))}
        </ul>

        <p className="text-sm text-gray-600 dark:text-gray-300">
          Do you still want to create this contact?
        </p>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={isSaving}>
            Create anyway
          </Button>
        </div>
      </div>
    </Modal>
  );
}
